/**
 * The Case Context Bar's Type field: the patient type on the pill, with a hover popover carrying the
 * type, an edit control where the coder may edit, and Legal status / Place of service rows.
 * `CaseContextBar` owns the `LABEL | value` pill chrome; this
 * renders the value half and the popover.
 *
 * `Popover` is used without `isLazy`, so its rows are always mounted and only visibility toggles
 * with hover/focus; tests can assert content without simulating hover.
 */
import {
  Box,
  Flex,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text
} from '@chakra-ui/react';
import { CMXIcon } from '@codametrix/ui-components';
import React from 'react';

import type { DictionaryOption } from '../../interface/api';
import { useCodingScreen } from '../../interface/context';
import type { PatientTypeInfoData } from '../../interface/patientTypeInfo';
import type { SlotProps } from '../../interface/slotProps';
import {
  INK,
  INK_MUTED,
  INK_SOFT,
  POPOVER_BORDER,
  POPOVER_RADIUS,
  POPOVER_SHADOW
} from '../../styles/tokens';
import {
  PatientTypeEditForm,
  type PatientTypeEditStatus
} from './PatientTypeEditForm';

const PLACEHOLDER = 'N/A';

const POPOVER_BG = '#ffffff';

/** Patient type is a COLUMN of the Patient Class dictionary, not a dictionary of its own. */
const PATIENT_TYPE_DICTIONARY = {
  dictionaryName: 'Patient Class',
  columnName: 'Patient Type',
  // The same rows already carry the class each type maps to, and the save writes both.
  pairedColumnName: 'Patient Class Original'
} as const;

const LOAD_FAILED = "Couldn't load the patient type list. Try again.";
const NO_OPTIONS = 'No patient types are available to choose from.';
const SAVE_FAILED = "Couldn't save the patient type. Try again.";

/**
 * Targets the ROWS, not the whole popover: `aria-describedby` flattens its target's subtree into one
 * description, so pointing at the body would re-announce the title and read the edit graphic as
 * prose. Lives on an inner Box because Chakra owns and overwrites `PopoverContent`'s `id`, and is
 * per-instance because two of these on one page would otherwise share it. The rows sit outside the
 * view/edit branch below, so this id keeps resolving while the editor is open.
 */
const useRowsId = (): string => `patient-type-info-rows-${React.useId()}`;

const HOVER_HINT_SX = {
  cursor: 'help',
  textDecoration: 'underline dotted var(--border-strong, #c9c8ce)',
  textUnderlineOffset: '3px',
  '&:hover, &:focus-visible': {
    textDecorationColor: 'var(--text-muted, #7a7682)'
  }
} as const;

/**
 * `Number.isFinite` rather than `typeof v === 'number'`: `NaN`/`Infinity` are numbers, and a typeof
 * check would paint their literal text as though it were a real patient type.
 */
const present = (v: unknown): v is string | number =>
  Number.isFinite(v) || (typeof v === 'string' && v.trim() !== '');

/** Present strings are NOT trimmed. */
const na = (v: unknown): string => (present(v) ? String(v) : PLACEHOLDER);

/** `value` is `unknown`, not `string`: the cast below is unchecked, so `na()` is what actually
 *  makes a wrong-shaped field safe. */
interface PatientTypeInfoRow {
  readonly id: string;
  readonly label: string;
  readonly value?: unknown;
}

const PatientTypeInfoRowView: React.FC<{
  readonly row: PatientTypeInfoRow;
}> = ({ row }) => (
  <Flex
    justifyContent="space-between"
    gap="20px"
    lineHeight="1.6"
    data-testid={`patient-type-info-${row.id}`}
  >
    {/* The label never wraps — a long value should yield, not "Place of / service". */}
    <Text
      as="span"
      fontSize="11px"
      fontWeight={400}
      color={INK_SOFT}
      flexShrink={0}
    >
      {row.label}
    </Text>
    <Text
      as="span"
      fontSize="11.5px"
      fontWeight={600}
      color={INK}
      // `space-between` positions the box, not the text inside it, so a wrapped second line needs
      // this to stay flush with the first.
      textAlign="right"
      // Both rules: `overflowWrap` handles long words, `wordBreak` keeps an unbroken run from
      // widening the fixed-width card.
      overflowWrap="anywhere"
      wordBreak="break-word"
    >
      {na(row.value)}
    </Text>
  </Flex>
);

export const PatientTypeInfo: React.FC<SlotProps> = ({
  value,
  mode: slotMode
}) => {
  const rowsId = useRowsId();
  const { api } = useCodingScreen();
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLSpanElement>(null);
  const selectRef = React.useRef<HTMLSelectElement>(null);

  // Anything that is not a genuine record collapses to `{}`, so a wrong-shaped value falls back to
  // the placeholder instead of reaching JSX as a non-primitive child.
  const data: PatientTypeInfoData =
    value != null && typeof value === 'object' && !Array.isArray(value)
      ? (value as PatientTypeInfoData)
      : {};

  const [mode, setMode] = React.useState<'view' | 'edit'>('view');
  const [isOpen, setIsOpen] = React.useState(false);
  const [status, setStatus] = React.useState<PatientTypeEditStatus>('loading');
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );
  const [options, setOptions] = React.useState<readonly DictionaryOption[]>([]);
  // A saved value the bound prop has not caught up with: the write settles before the case refetch
  // lands, and without this the field would snap back to the old type in between.
  // Covers the patient type ONLY. The same save also writes `patient_class`, so until the refetch
  // lands the Legal status row still shows the class it replaced. Accepted: a second optimistic
  // channel would need its own reconciliation guard to close a sub-second window.
  const [displayOverride, setDisplayOverride] = React.useState<
    string | undefined
  >(undefined);

  const shownValue = displayOverride ?? data.patientType;
  const patientType = na(shownValue);

  const rows: readonly PatientTypeInfoRow[] = [
    { id: 'legal-status', label: 'Legal status', value: data.legalStatus },
    {
      id: 'place-of-service',
      label: 'Place of service',
      value: data.placeOfService
    }
  ];

  const canEdit = data.permission === 'allowed' && slotMode === 'coding';
  const canSave = Boolean(
    canEdit && api?.updatePatientType && data.caseRunUid && data.serviceLine
  );

  // `setIsOpen(true)` is not incidental: `Popover` is controlled here, its content starts closed,
  // and Chakra's hover trigger attaches no click handler -- so flipping `mode` alone would leave
  // the form mounted but invisible.
  const enterEdit = React.useCallback(() => {
    if (!canEdit) return;
    setMode('edit');
    setIsOpen(true);
  }, [canEdit]);

  const editSession = React.useRef(0);
  const committedValue = React.useRef<string | undefined>(undefined);

  const resetEditSession = React.useCallback(() => {
    editSession.current += 1;
    setMode('view');
    setStatus('loading');
    setErrorMessage(undefined);
    setOptions([]);
  }, []);

  const leaveEdit = React.useCallback(() => {
    resetEditSession();
    triggerRef.current?.focus();
  }, [resetEditSession]);

  // Chakra's focus hooks are inert here: `useFocusOnShow` runs in a `useUpdateEffect`
  // that skips mount, and the panel is already mounted. Focus is driven from this
  // component instead. Entry is UNCONDITIONAL: on the keyboard and click paths
  // focus is still on the trigger, which a guard would mistake for someone else
  // owning it.
  React.useEffect(() => {
    if (mode !== 'edit') return undefined;
    const id = requestAnimationFrame(() => {
      if (selectRef.current && !selectRef.current.disabled) {
        selectRef.current.focus();
      } else {
        popoverRef.current?.focus();
      }
    });
    return () => cancelAnimationFrame(id);
  }, [mode]);

  // The select is disabled until the dictionary lands, and a case refetch swaps `api`
  // and sends it back to disabled -- so hand focus over whenever it becomes usable,
  // but only from where this component parked it. `shownValue` is a dependency
  // because it keys the form below: our own save coming back keeps the editor open
  // but remounts it, destroying the focused select and leaving `activeElement` on
  // `<body>` without `mode` or `status` moving.
  React.useEffect(() => {
    if (mode !== 'edit' || status !== 'ready') return;
    const active = document.activeElement;
    if (active !== document.body && active !== popoverRef.current) return;
    selectRef.current?.focus();
  }, [mode, status, shownValue]);

  // Advisory while editing: the hover branch schedules unguarded close timers
  // that survive the trigger flip.
  const handleClose = React.useCallback(() => {
    if (mode === 'edit') return;
    setIsOpen(false);
  }, [mode]);

  React.useEffect(() => {
    if (!isOpen || status === 'saving') return undefined;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setIsOpen(false);
      resetEditSession();
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isOpen, status, resetEditSession]);

  React.useEffect(() => {
    if (mode !== 'edit') return undefined;

    const load = api?.loadDictionaryOptions;
    if (!load) {
      setOptions([]);
      setStatus('unavailable');
      setErrorMessage(LOAD_FAILED);
      return undefined;
    }

    // Its own flag, deliberately NOT the `editSession` counter: this one tracks a single effect run
    // (a re-render with a fresh `api` abandons the fetch without ending the coder's edit), the
    // counter tracks the edit itself.
    let abandoned = false;
    setStatus('loading');
    setErrorMessage(undefined);
    load(PATIENT_TYPE_DICTIONARY)
      .then((loaded) => {
        if (abandoned) return;
        setOptions(loaded);
        setStatus(loaded.length > 0 ? 'ready' : 'unavailable');
        setErrorMessage(loaded.length > 0 ? undefined : NO_OPTIONS);
      })
      .catch(() => {
        if (abandoned) return;
        setOptions([]);
        setStatus('unavailable');
        setErrorMessage(LOAD_FAILED);
      });

    return () => {
      abandoned = true;
    };
  }, [mode, api]);

  React.useEffect(() => {
    if (mode === 'edit' && !canEdit) leaveEdit();
  }, [mode, canEdit, leaveEdit]);

  const caseRunUid = React.useRef(data.caseRunUid);
  React.useEffect(() => {
    if (caseRunUid.current === data.caseRunUid) return;
    caseRunUid.current = data.caseRunUid;
    // A different case must not inherit the previous one's optimistic value.
    setDisplayOverride(undefined);
    if (mode === 'edit') leaveEdit();
  }, [data.caseRunUid, mode, leaveEdit]);

  const boundValue = React.useRef(data.patientType);
  React.useEffect(() => {
    if (boundValue.current === data.patientType) return;
    // Defer while a save is in flight -- advancing the ref here would swallow the
    // change instead of acting on it once the write settles.
    if (status === 'saving') return;
    const wasOurs = committedValue.current === data.patientType;
    boundValue.current = data.patientType;
    committedValue.current = undefined;
    setDisplayOverride(undefined);
    if (mode === 'edit' && !wasOurs) leaveEdit();
  }, [data.patientType, mode, status, leaveEdit]);

  const onSave = async (newValue: string) => {
    const update = api?.updatePatientType;
    if (!canSave || !update || !data.caseRunUid || !data.serviceLine) {
      return;
    }
    const session = editSession.current;
    setStatus('saving');
    setErrorMessage(undefined);
    try {
      await update({
        caseRunUid: data.caseRunUid,
        serviceLine: data.serviceLine,
        newValue,
        patientClass: options.find((o) => o.value === newValue)?.pairedValue
      });
    } catch {
      // The coder can abandon, or the case change, while the PUT is outstanding;
      // this settlement then belongs to a session that no longer exists.
      if (editSession.current !== session) return;
      // Back to `ready`, not merely an error: without it Save stays disabled and the
      // panel is stuck with the coder's selection unusable.
      setStatus('ready');
      setErrorMessage(SAVE_FAILED);
      return;
    }
    if (editSession.current !== session) return;
    committedValue.current = newValue;
    setDisplayOverride(newValue);
    leaveEdit();
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={handleClose}
      trigger={mode === 'edit' ? 'click' : 'hover'}
      closeOnBlur={mode !== 'edit'}
      closeOnEsc={mode !== 'edit'}
      placement="bottom-start"
    >
      <PopoverTrigger>
        <Text
          as="span"
          ref={triggerRef}
          tabIndex={0}
          onKeyDown={(event) => {
            if (!canEdit) return;
            if (event.key === 'Enter' || event.key === ' ') {
              // `callAllHandlers` short-circuits on `defaultPrevented`, which is what keeps
              // Chakra's own trigger `onKeyDown` off this event -- and Space off the page scroll.
              event.preventDefault();
              enterEdit();
            }
          }}
          // NOT `role="button"`: the handler above is the KEYBOARD entry into edit mode and the
          // popover's icon button is the pointer one, so a button role would over-promise in the
          // other direction — this span is deliberately not click-activated.
          // The pill's "Type" caption is a sibling node owned by `CaseContextBar`, so without this
          // a focused trigger announces a bare value — or, on an absent value, just "N/A".
          aria-label={`Patient type: ${patientType}`}
          aria-describedby={rowsId}
          data-testid="patient-type-info"
          textStyle="Body 2"
          color={INK}
          // Truncation lives here, not on the host `Tag`: the patient type is the pill's only
          // unbounded value. `display` is explicit because `max-width`/`overflow`/`text-overflow`
          // do not apply to a non-replaced inline box, and `minWidth={0}` is what lets
          // `textOverflow` take effect inside the host's flex row.
          display="inline-block"
          maxWidth="14rem"
          minWidth={0}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          sx={HOVER_HINT_SX}
        >
          {patientType}
        </Text>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          ref={popoverRef}
          onKeyDown={(event) => {
            if (mode !== 'edit') return;
            if (event.key === 'Escape') {
              event.stopPropagation();
              leaveEdit();
            }
          }}
          data-testid="patient-type-info-popover"
          data-edit-permission={data.permission ?? 'pending'}
          bg={POPOVER_BG}
          border={`1px solid ${POPOVER_BORDER}`}
          borderRadius={POPOVER_RADIUS}
          boxShadow={POPOVER_SHADOW}
          padding="14px 16px"
          // Both props: `width` alone loses to Chakra's own `PopoverContent` max-width default.
          width="280px"
          maxWidth="280px"
        >
          <PopoverArrow bg={POPOVER_BG} />

          {mode === 'edit' ? (
            // Keyed on the displayed value: the form seeds its selection from `currentValue` once,
            // so it has to be rebuilt whenever the value underneath it moves.
            <PatientTypeEditForm
              key={String(shownValue ?? '')}
              currentValue={typeof shownValue === 'string' ? shownValue : ''}
              options={options}
              status={status}
              errorMessage={errorMessage}
              canSave={canSave}
              selectRef={selectRef}
              onSave={onSave}
              onCancel={leaveEdit}
            />
          ) : (
            <Flex align="center" justifyContent="space-between" gap="12px">
              <Text
                as="span"
                textStyle="Body 1"
                color={INK}
                data-testid="patient-type-info-title"
                minWidth={0}
                // The title WRAPS where the trigger ellipsises. Its usable width (~208px after
                // padding, border, the icon and the gap) is narrower than the trigger's 14rem, so
                // truncating here too would leave a long type unreadable anywhere in the app.
                overflowWrap="anywhere"
                wordBreak="break-word"
              >
                {patientType}
              </Text>

              {canEdit && (
                <IconButton
                  aria-label="Edit patient type"
                  data-testid="patient-type-info-edit"
                  icon={<CMXIcon svgIconName="BiomeEdit" svgIconSize="12" />}
                  onClick={enterEdit}
                  // `unstyled` removes variant padding and height, so these geometry props
                  // alone size the box.
                  variant="unstyled"
                  cursor="pointer"
                  flexShrink={0}
                  // Grid centering keeps the 12px icon from sitting top-left in its
                  // 26px box.
                  display="grid"
                  placeItems="center"
                  minWidth="26px"
                  w="26px"
                  h="26px"
                  color={INK_MUTED}
                  border="1px solid var(--border-strong, #c9c8ce)"
                  borderRadius="7px"
                />
              )}
            </Flex>
          )}

          <Box
            borderTop={`1px solid ${POPOVER_BORDER}`}
            marginTop="12px"
            paddingTop="12px"
            data-testid="patient-type-info-divider"
          >
            <Flex
              id={rowsId}
              direction="column"
              gap="6px"
              data-testid="patient-type-info-rows"
            >
              {rows.map((row) => (
                <PatientTypeInfoRowView key={row.id} row={row} />
              ))}
            </Flex>
          </Box>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
